import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';

import { UiInitialState, UiStateModel } from '@store/models/ui.model';
import { UiActions } from '@store/actions/ui.actions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';

@State<UiStateModel>({
  name: 'UiState',
  defaults: UiInitialState,
})
@Injectable()
export class UiState {
  constructor(
    private readonly messageService: MessageService,
    private readonly confirmationService: ConfirmationService,
    private readonly translate: TranslateService,
  ) {
  }

  @Action(UiActions.openSidebar)
  openSidebar(ctx: StateContext<UiStateModel>) {
    /**
     * Open the sidebar component
     * TODO render a component in the sidebar
     */
    ctx.patchState({ sideBarOpen: true });
  }

  @Action(UiActions.closeSidebar)
  closeSidebar(ctx: StateContext<UiStateModel>) {
    ctx.patchState({ sideBarOpen: false });
  }

  @Action(UiActions.toasterShowError)
  toasterShowError(_ctx: StateContext<UiStateModel>, action: UiActions.toasterShowError) {
    this.messageService.add({
      severity: 'error',
      summary: action.payload.summary,
      detail: this.isTranslationKey(action.payload.message),
      sticky: action.payload.sticky,
      data: {
        testKey: action.payload.testKey,
      },
    });
  }

  @Action(UiActions.toasterShowSuccess)
  toasterShowSuccess(_ctx: StateContext<UiStateModel>, action: UiActions.toasterShowSuccess) {
    this.messageService.add({
      severity: 'success',
      summary: action.payload.summary,
      detail: this.isTranslationKey(action.payload.message),
      sticky: action.payload.sticky,
      data: {
        testKey: action.payload.testKey,
      },
    });
  }

  @Action(UiActions.toasterShowInfo)
  toasterShowInfo(_ctx: StateContext<UiStateModel>, action: UiActions.toasterShowInfo) {
    this.messageService.add({
      severity: 'info',
      summary: action.payload.summary,
      detail: this.isTranslationKey(action.payload.message),
      sticky: action.payload.sticky,
      data: {
        testKey: action.payload.testKey,
      },
    });
  }

  @Action(UiActions.toasterShowWarn)
  toasterShowWarn(_ctx: StateContext<UiStateModel>, action: UiActions.toasterShowWarn) {
    this.messageService.add({
      severity: 'warn',
      summary: action.payload.summary,
      detail: this.isTranslationKey(action.payload.message),
      sticky: action.payload.sticky,
      data: {
        testKey: action.payload.testKey,
      },
    });
  }

  @Action(UiActions.toasterClearAll)
  toasterClearAll() {
    this.messageService.clear();
  }

  @Action(UiActions.confirmDialogOpen)
  confirmDialogOpen(_ctx: StateContext<UiStateModel>, action: UiActions.confirmDialogOpen) {
    action.confirmation.acceptButtonStyleClass = action.confirmation.acceptButtonStyleClass + ' p-button-rounded';
    action.confirmation.rejectButtonStyleClass = 'p-button-secondary p-button-rounded p-button-outlined';
    this.confirmationService.confirm(action.confirmation);
  }

  @Action(UiActions.openOnScreenInfo)
  openOnScreenInfo(ctx: StateContext<UiStateModel>) {
    ctx.patchState({ infoOnScreenOpen: true });
  }

  @Action(UiActions.closeOnScreenInfo)
  closeOnScreenInfo(ctx: StateContext<UiStateModel>) {
    ctx.patchState({ infoOnScreenOpen: false });
  }

  @Action(UiActions.openAppInfo)
  openAppInfo(ctx: StateContext<UiStateModel>) {
    ctx.patchState({ appInfoOpen: true });
  }

  @Action(UiActions.closeAppInfo)
  closeAppInfo(ctx: StateContext<UiStateModel>) {
    ctx.patchState({ appInfoOpen: false });
  }

  @Action(UiActions.toggleOnScreenInfo)
  toggleOnScreenInfo(ctx: StateContext<UiStateModel>) {
    const state = ctx.getState();
    ctx.patchState({ infoOnScreenOpen: !state.infoOnScreenOpen });
  }

  @Action(UiActions.toggleStartcodesMasked)
  setStartcodesMasked(ctx: StateContext<UiStateModel>) {
    const state = ctx.getState();
    ctx.patchState({ startcodesMasked: !state.startcodesMasked });
  }

  @Action(UiActions.setForbidden403Access)
  setForbidden403Access(ctx: StateContext<UiStateModel>) {
    ctx.patchState({ forbidden403Access: true });
  }

  private isTranslationKey(msg: string) {
    return /^[A-Z_.]+$/.test(msg) && msg.includes('.') ? this.translate.instant('TOASTER_MESSAGES.GENERIC_MSG') : msg;
  }
}
